import { UrlRouteMap } from "@shared/services/api/types";
import Repository, {
  ExtendedAxiosRequestConfig,
} from "@shared/services/api/abstracts/Repository";
import { TicketBasket } from "@shared/modules/basket/types";
import { logAppInfo, recordAppError } from "@shared/utils/errors/errorLogger";
import { YunoCheckoutSessionResponse } from "@shared/modules/payments/types/yuno";
import {
  GetBasketHoldParamType,
  UserBasedTicketBasketHoldsSerializer,
  UserBasedTicketBasketPaymentIntentSerializer,
  UserBasedTicketBasketPurchaseRequest,
  UserBasedTicketBasketPurchaseResponse,
  UserBasedTicketBasketSerializer,
  UserBasedTicketBasketSetupIntentSerializer,
} from "../../types/basket";

class UserBasedTicketBasketRepository extends Repository<UserBasedTicketBasketSerializer> {
  public readonly repositoryName = "UserBasedTicketBasketRepository";

  public readonly endpoint = "api/user/tickets/baskets/";

  public get urlMap(): UrlRouteMap {
    return {
      ...this.defaultUrlMap,
      purchase: "{id}/purchase/",
      reset: "{id}/reset/",
      upsertPaymentIntent: "{id}/stripe/payment-intent/",
      upsertSetupIntent: "{id}/stripe/setup-intent/",
      holdDetails: "holds/{link}/",
      claim: "claim/{id}/",
      yunoCheckoutSession: "{id}/yuno/checkout-session/",
    };
  }

  /**
   * Creates or updates a payment intent for a basket
   */
  public async upsertPaymentIntent({
    id,
    uuid,
  }: {
    id: number;
    uuid: string;
  }): Promise<UserBasedTicketBasketPaymentIntentSerializer> {
    const { data } =
      await this.request<UserBasedTicketBasketPaymentIntentSerializer>(
        this.buildUrl("upsertPaymentIntent", {
          id,
        }),
        {
          data: { id, uuid },
          method: "POST",
        }
      );

    return data;
  }

  /**
   * Creates or updates a setup intent for a basket
   */
  public async upsertSetupIntent({
    id,
    uuid,
  }: {
    id: number;
    uuid: string;
  }): Promise<UserBasedTicketBasketSetupIntentSerializer> {
    try {
      logAppInfo("Initiating setup intent creation", {
        category: "SetupIntent",
        data: { basketId: id, basketUuid: uuid },
      });

      const { data } =
        await this.request<UserBasedTicketBasketSetupIntentSerializer>(
          this.buildUrl("upsertSetupIntent", {
            id,
          }),
          {
            data: { id, uuid },
            method: "POST",
          }
        );

      logAppInfo("Setup intent created successfully", {
        category: "SetupIntent",
        data: {
          basketId: id,
          basketUuid: uuid,
          setupIntentId: data.id,
        },
      });

      return data;
    } catch (error) {
      logAppInfo("Setup intent creation failed", {
        category: "SetupIntent",
        level: "error",
        data: { basketId: id, basketUuid: uuid },
      });
      recordAppError(error);
      throw error;
    }
  }

  /**
   * Creates a Yuno checkout session for the basket
   * Used by both user-based and venue-based baskets
   */
  public async createYunoCheckoutSession({
    id,
    uuid,
  }: {
    id: number;
    uuid: string;
  }): Promise<YunoCheckoutSessionResponse> {
    const { data } = await this.request<YunoCheckoutSessionResponse>(
      this.buildUrl("yunoCheckoutSession", { id }),
      {
        data: { uuid },
        method: "POST",
      }
    );

    return data;
  }

  public async purchase(
    id: Exclude<TicketBasket["id"], undefined>,
    attributes: UserBasedTicketBasketPurchaseRequest,
    config: ExtendedAxiosRequestConfig = {}
  ) {
    const { data } = await this.request<UserBasedTicketBasketPurchaseResponse>(
      this.buildUrl("purchase", {
        id,
      }),
      {
        data: { id, ...attributes },
        method: "POST",
        ...config,
      }
    );

    return data;
  }

  public async reset(
    id: Exclude<TicketBasket["id"], undefined>,
    config: ExtendedAxiosRequestConfig = {}
  ) {
    const { data } = await this.request(
      this.buildUrl("reset", {
        id,
      }),
      {
        data: { id },
        method: "POST",
        ...config,
      }
    );

    return data;
  }

  public async holdDetails(
    link: string,
    attributes?: GetBasketHoldParamType
  ): Promise<UserBasedTicketBasketHoldsSerializer> {
    const { data } = await this.request(
      this.buildUrl("holdDetails", {
        link,
      }),
      {
        data: { link, ...attributes },
        method: "GET",
      }
    );
    return data;
  }

  public async claim(
    uuid: string
  ): Promise<UserBasedTicketBasketHoldsSerializer> {
    const { data } = await this.request(this.buildUrl("claim"), {
      data: { uuid },
      method: "POST",
    });
    return data;
  }
}

export default UserBasedTicketBasketRepository;
